import React, { useCallback, useMemo } from 'react'
import { Box, Paper, Typography } from '@material-ui/core'
import _ from 'lodash'
import { RuleStepProps } from '../../CampaignModalSkeleton/CampaignModalSkeleton.types'
import { hasPaidPrice, Product, toggleArrayItem } from '../../MarketingCampaigns.promo.services'
import { BNGM_SCOPE, BuyNGetMRuleState, ScopeType } from './types'
import {
	CampaignOptionList,
	NumSpinner,
	ParamRow,
	ProductScopeSelector,
	RuleParamsPanel,
} from '../../CampaignModalSkeleton/components'
import {
	CAMPAIGN_ACCENT_ORANGE,
	G,
} from '../../CampaignModalSkeleton/CampaignModalSkeleton.styles'
import { isOptionListAllowedBNGM } from './RuleStep.services'
import { BNGM_OPTION_LISTS_EMPTY_MESSAGE, BNGM_OPTION_LISTS_ERROR_MESSAGE, BNGM_OPTION_LISTS_LOADING_MESSAGE } from './bngmOptionLists'

export function BuyNGetMRuleStep({
	rule,
	setRule,
	description,
	sections,
	allMenus,
	selectedShopIds,
	bngmOptionListEligibility,
}: RuleStepProps<BuyNGetMRuleState>) {
	const handleToggle = useCallback(
		(product: Product, sectionId: string) => {
			setRule((r) => {
				const formItem = { productKey: product.key, productSectionKey: sectionId }
				const nextProducts = toggleArrayItem(r.products, formItem, {
					isEqual: (a, b) => a.productKey === b.productKey,
				})
				return {
					...r,
					products: nextProducts,
					selectedProductKeys: nextProducts.map((p) => p.productKey),
				}
			})
		},
		[setRule],
	)


	const emptyOptionListsMessage = useMemo(() => {
		if (bngmOptionListEligibility?.loading) {
			return BNGM_OPTION_LISTS_LOADING_MESSAGE
		}
		if (bngmOptionListEligibility?.error) {
			return BNGM_OPTION_LISTS_ERROR_MESSAGE
		}
		return BNGM_OPTION_LISTS_EMPTY_MESSAGE
	}, [bngmOptionListEligibility?.error, bngmOptionListEligibility?.loading])

	const optionLists = useMemo((): CampaignOptionList[] => {
		if (!allMenus || !selectedShopIds || selectedShopIds.length === 0) return []
		const categoryKeysByShop = selectedShopIds.map(
			(sid) => Object.keys((allMenus[sid] as any)?.categories || {}),
		)
		const commonKeys = categoryKeysByShop.reduce((acc, keys) =>
			acc.filter((k) => keys.includes(k)),
		)
		const firstShop = allMenus[selectedShopIds[0]]
		if (!firstShop || !(firstShop as any).categories) return []
		const cats = (firstShop as any).categories as Record<string, any>
		return commonKeys
			.map((key): CampaignOptionList | null => {
				const cat = cats[key]
				const items = Object.values(cat.items || {}) as any[]
				if (items.length === 0) return null
				const allPriced = items.every(hasPaidPrice)
				if (!allPriced) return null
				if (!isOptionListAllowedBNGM(cat)) return null
				return {
					id: cat.key,
					name: cat.name,
					count: items.length,
					type: cat.type,
					isPrivate: cat.isPrivate ?? false,
					options: items.map((item) => ({
						key: item.key,
						name: item.name,
						price: item.price,
					})),
				}
			})
			.filter((ol): ol is CampaignOptionList => ol !== null)
	}, [allMenus, selectedShopIds])

	const handleScopeChange = useCallback(
		(tab: ScopeType) => {
			setRule((r) => ({
				...r,
				scopeType: tab,
				products: tab === BNGM_SCOPE.PRODUCTS ? r.products : [],
				selectedProductKeys: tab === BNGM_SCOPE.PRODUCTS ? r.selectedProductKeys : [],
				selectedOptionListKey:
					tab === BNGM_SCOPE.OPTIONS ? r.selectedOptionListKey : null,
			}))
		},
		[setRule],
	)

	const handleOptionListSelect = useCallback(
		(key: string) => {
			setRule((r) => ({ ...r, selectedOptionListKey: key }))
		},
		[setRule],
	)

	const handleNChange = useCallback(
		(v: number | null) => {
			if (v === null) return
			setRule((r) => ({ ...r, numProductsPaid: v }))
		},
		[setRule],
	)

	const handleMChange = useCallback(
		(v: number | null) => {
			if (v === null) return
			setRule((r) => ({ ...r, numProductsDiscounted: v }))
		},
		[setRule],
	)

	const handlePctChange = useCallback(
		(v: number | null) => {
			if (v === null) return
			setRule((r) => ({ ...r, discountPercent: v }))
		},
		[setRule],
	)

	const hasProducts = _.size(sections) > 0
	const hasEligibleOptionLists =
		!bngmOptionListEligibility?.loading &&
		!bngmOptionListEligibility?.error &&
		optionLists.length > 0
	const isOptionListsPending = Boolean(bngmOptionListEligibility?.loading)
	const hasOptionListsError = Boolean(bngmOptionListEligibility?.error)

	if (
		!hasProducts &&
		!isOptionListsPending &&
		!hasEligibleOptionLists &&
		!hasOptionListsError
	) {
		return (
			<Box>
				{description}
				<Typography variant="body2" color="textSecondary" className="py-4">
					Aucun produit ou liste d&apos;options en commun n&apos;est disponible. Merci de
					sélectionner à nouveau les points de vente ciblés.
				</Typography>
			</Box>
		)
	}

	return (
		<Box>
			{description}
			<ProductScopeSelector
				sections={sections}
				selectedKeys={rule.selectedProductKeys}
				showRankBadge={false}
				onToggle={handleToggle}
				instructions="Sélectionnez les produits éligibles à la promotion"
				withOptionsTab
				optionLists={optionLists}
				activeScopeTab={rule.scopeType}
				selectedOptionListKey={rule.selectedOptionListKey}
				onOptionListSelect={handleOptionListSelect}
				onScopeTabChange={handleScopeChange}
				emptyOptionListsMessage={emptyOptionListsMessage}
				optionListsLoading={isOptionListsPending}
				optionListsError={hasOptionListsError}
			/>
			<RuleParamsPanel>
				<ParamRow
					label="Articles payants"
					description="Nombre d'articles payants dans le panier"
				>
					<NumSpinner
						value={rule.numProductsPaid}
						onChange={handleNChange}
						min={1}
					/>
				</ParamRow>
				<ParamRow
					label="Articles remisés"
					description="Nombre d'articles sur lesquels s'applique la remise"
				>
					<NumSpinner
						value={rule.numProductsDiscounted}
						onChange={handleMChange}
						min={1}
					/>
				</ParamRow>
				<ParamRow
					label="Remise appliquée"
					description="Montant de la remise"
					badge="% uniquement"
					isLast
				>
					<NumSpinner
						value={rule.discountPercent}
						onChange={handlePctChange}
						min={1}
						max={100}
						suffix="%"
						step="any"
					/>
				</ParamRow>
			</RuleParamsPanel>
			<Paper
				variant="outlined"
				style={{
					borderRadius: 10,
					padding: 12,
					background: '#fafafa',
					border: '1px solid rgba(0,0,0,0.08)',
				}}
			>
				<Typography variant="body2" style={{ color: '#616161', lineHeight: 1.7, fontSize: 14 }}>
					<span style={{ color: G, fontWeight: 600 }}>
						{rule.numProductsPaid + rule.numProductsDiscounted}
					</span>{' '}
					produits éligibles dans le panier ={' '}
					<span style={{ color: G, fontWeight: 600 }}>{rule.numProductsDiscounted}</span>{' '}
					produit(s) remisé(s) à{' '}
					<span style={{ color: CAMPAIGN_ACCENT_ORANGE, fontWeight: 600 }}>
						{rule.discountPercent}%
					</span>
				</Typography>
			</Paper>
		</Box>
	)
}
